import { Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnAnalyticsApi } from "../../../apis";
import { currencyFormat } from "../../../utils/currency";
import { ListState } from "../../ui";
import { DetailCard } from "../detail-card";

interface IProps {
  dateFromIso?: string;
  dateToIso?  : string;
  limit?      : number;
}

const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

export function TopProductsList({ dateFromIso, dateToIso, limit = 10 }: IProps) {
  const { t } = useTranslation("returns");
  const { data, error, mutate } = useSWR(
    ["returns.top-products", dateFromIso, dateToIso, limit],
    () => ReturnAnalyticsApi.topProducts({ dateFrom: dateFromIso, dateTo: dateToIso, limit }),
    { revalidateOnFocus: false }
  );

  const rows = data?.products ?? [];

  return (
    <DetailCard title={t("dashboard.topProducts.title")}>
      {error && !data ? (
        <ListState state="error" onRetry={() => mutate()} />
      ) : rows.length === 0 ? (
        <ListState state="empty" title={t("dashboard.topProducts.empty")} />
      ) : (
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("col.product")}</Table.Th>
              <Table.Th ta="right">{t("col.returns")}</Table.Th>
              <Table.Th ta="right">{t("col.amount")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((p) => (
              <Table.Tr key={p.productId ?? p.productName}>
                <Table.Td>
                  <Text size="sm">{p.productName || "-"}</Text>
                  {p.sku ? <Text size="sm" c="var(--nw-text-2)" ff="monospace">{p.sku}</Text> : null}
                </Table.Td>
                <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{p.returnCount}</Text></Table.Td>
                <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{currencyFormat(p.refundAmount ?? 0)}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </DetailCard>
  );
}
